<script>
import { GlIcon, GlLink } from '@gitlab/ui';
import { upperCase } from 'lodash-es';
import AgentStatusIcon from 'ee/ai/shared/widgets/agent_status_icon.vue';
import TodoChecklist from 'ee/ai/duo_agents_platform/components/common/todo_checklist.vue';
import getFlowStatusQuery from 'ee/ai/graphql/get_flow_status.query.graphql';
import getWorkflowLatestCheckpointQuery from 'ee/ai/graphql/get_workflow_latest_checkpoint.query.graphql';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { TYPENAME_AI_DUO_WORKFLOW } from '~/graphql_shared/constants';
import { visitUrl } from '~/lib/utils/url_utility';
import { captureExceptionForDuoChat } from '../../observability/sentry_utils';
import { EventsTracker } from '../../observability/events_tracker';
import { WorkflowUtils } from '../../utils/workflow_utils';

const POLL_INTERVAL = 10000;
const TERMINAL_STATUSES = ['FINISHED', 'FAILED', 'STOPPED'];

const PILL_CLASSES = [
  'gl-border',
  'gl-inline-flex',
  '!gl-w-fit',
  'gl-items-center',
  'gl-gap-2',
  'gl-rounded-r-[1rem]',
  'gl-rounded-tl-[1rem]',
  'gl-border-default',
  'gl-bg-default',
  'gl-p-4',
];

const EXPANDED_CARD_CLASSES = [
  'gl-flex',
  'gl-w-full',
  'gl-flex-col',
  'gl-gap-3',
  'gl-rounded-r-[1rem]',
  'gl-rounded-tl-[1rem]',
  'gl-border',
  'gl-border-default',
  'gl-bg-default',
  'gl-p-4',
];

export default {
  name: 'MessageToolStartFlow',
  components: {
    AgentStatusIcon,
    GlIcon,
    GlLink,
    TodoChecklist,
  },
  apollo: {
    flowStatus: {
      query: getFlowStatusQuery,
      variables() {
        return { id: convertToGraphQLId(TYPENAME_AI_DUO_WORKFLOW, this.workflowId) };
      },
      skip() {
        return !this.workflowId || TERMINAL_STATUSES.includes(this.internalStatus);
      },
      pollInterval: POLL_INTERVAL,
      update(data) {
        return data.duoWorkflowWorkflows?.edges?.[0]?.node?.status ?? null;
      },
      error(error) {
        captureExceptionForDuoChat(error);
      },
    },
    flowPlan: {
      query: getWorkflowLatestCheckpointQuery,
      variables() {
        return { workflowId: convertToGraphQLId(TYPENAME_AI_DUO_WORKFLOW, this.workflowId) };
      },
      skip() {
        return !this.workflowId || TERMINAL_STATUSES.includes(this.internalStatus);
      },
      pollInterval: POLL_INTERVAL,
      update(data) {
        const checkpoint = WorkflowUtils.parseWorkflowData(data);
        const messages = WorkflowUtils.normalizeDuoMessages(checkpoint?.duoMessages ?? []);
        return WorkflowUtils.findLatestTodoToolInfo(messages);
      },
      error(error) {
        captureExceptionForDuoChat(error);
      },
    },
  },
  props: {
    message: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      flowStatus: null,
      flowPlan: null,
    };
  },
  computed: {
    toolResponse() {
      try {
        return JSON.parse(this.message.tool_info?.tool_response?.content ?? '{}');
      } catch {
        return {};
      }
    },
    flowName() {
      return this.toolResponse.flow_name ?? '';
    },
    workflowId() {
      return this.toolResponse.workflow_id ?? null;
    },
    sessionUrl() {
      return this.toolResponse.session_url ?? null;
    },
    internalStatus() {
      return this.flowStatus ?? upperCase(this.toolResponse?.status);
    },
    humanStatus() {
      return (this.internalStatus ?? '').toLowerCase().replaceAll('_', ' ');
    },
    toolName() {
      return this.message.tool_info?.name ?? '';
    },
    hasPlan() {
      return Boolean(this.flowPlan);
    },
    wrapperProps() {
      const baseClasses = this.hasPlan ? EXPANDED_CARD_CLASSES : PILL_CLASSES;
      if (this.sessionUrl) {
        return {
          href: this.sessionUrl,
          target: '_blank',
          class: [...baseClasses, '!gl-text-default !gl-no-underline hover:!gl-text-default'],
        };
      }
      return { class: baseClasses };
    },
  },
  methods: {
    handleSessionUrlClick(event) {
      if (!this.sessionUrl) return;
      event.preventDefault();
      EventsTracker.trackClickThroughFlowWidget({ toolName: this.toolName });
      visitUrl(this.sessionUrl, true);
    },
  },
};
</script>

<template>
  <component
    :is="sessionUrl ? 'gl-link' : 'div'"
    v-bind="wrapperProps"
    data-testid="start-flow-message"
    @click="handleSessionUrlClick"
  >
    <div class="gl-flex gl-items-center gl-gap-2">
      <span aria-hidden="true">
        <agent-status-icon :status="internalStatus" :human-status="humanStatus" />
      </span>
      <span :class="{ 'gl-flex-1': hasPlan && sessionUrl }">
        {{ flowName }}<template v-if="workflowId"> #{{ workflowId }}</template>
        <strong class="gl-ml-1">{{ humanStatus }}</strong>
      </span>
      <gl-icon v-if="sessionUrl" name="chevron-right" aria-hidden="true" />
    </div>
    <todo-checklist v-if="hasPlan" :tool-info="flowPlan" :bordered="false" />
  </component>
</template>
